import React from 'react';
import { PdfGridsArea, PdfH1, PdfP, PdfPage, PdfTable, PdfTdLabel } from './Styles.styled';
import isFalsyOrEmptyHtmlRichText from 'util/isFalsyOrEmptyHtmlRichText';
import usePdfData from './utils/usePdfData';
import usePcrV2 from 'components/v2/compiler/hooks/usePcrV2';
import { EPDProductClassificationId } from 'components/v2/compiler/constants';
import { NOT_ASSIGNED } from 'constants/constants';

const AdditionalInfoPage: React.FC = () => {
  const { epdDefinition, additionalInformation } = usePdfData();
  const isPcrV2 = usePcrV2();

  const possibleSections: (React.ReactElement | false | null)[] = [
    !isFalsyOrEmptyHtmlRichText(additionalInformation?.environmental) && (
      <>
        <PdfH1>Additional environmental information</PdfH1>
        <PdfP dangerouslySetInnerHTML={{ __html: additionalInformation.environmental.replace(/\n/g, '') }} />
      </>
    ),

    !isFalsyOrEmptyHtmlRichText(additionalInformation?.economic) && (
      <>
        <PdfH1>Additional social and economic information</PdfH1>
        <PdfP dangerouslySetInnerHTML={{ __html: additionalInformation.economic.replace(/\n/g, '') }} />
      </>
    ),

    (() => {
      const epdClassification = epdDefinition.epdClassification;
      const possibleRows = [
        (epdClassification === EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_REPRESENTATIVE_PRODUCT_ID ||
          epdClassification ===
            EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_SEVERAL_REPRESENTATIVE_PRODUCTS_ID) && {
          name: 'Justification for why this is representative',
          value: epdDefinition.representativeJustification ?? NOT_ASSIGNED,
        },
        epdClassification === EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_AVG_RESULTS_ID && {
          name: 'Description of how the averages have been determined',
          value: epdDefinition.determinationOfAverageDescription ?? NOT_ASSIGNED,
        },
      ];

      const rows = possibleRows.filter(Boolean) as { name: string; value: string }[];

      if (rows.length === 0) {
        return null;
      }

      return (
        <>
          <PdfH1>Information related to EPDs of multiple products</PdfH1>
          <PdfTable style={{ marginBottom: '1em' }}>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <PdfTdLabel>{r.name}</PdfTdLabel>
                  <td>{r.value}</td>
                </tr>
              ))}
            </tbody>
          </PdfTable>
        </>
      );
    })(),

    (() => {
      const isSectorEpd =
        epdDefinition.epdClassification === EPDProductClassificationId.SECTOR_OF_MULTIPLE_PRODUCTS_ID ||
        epdDefinition.epdClassification === EPDProductClassificationId.SECTOR_OF_AVERAGE_PRODUCTS_ID ||
        epdDefinition.epdClassification === EPDProductClassificationId.SECTOR_OF_WORST_CASE_PRODUCTS_ID;
      if (!isSectorEpd) {
        return null;
      }

      return (
        <>
          <PdfH1>Information related to Sector EPDs</PdfH1>
          <PdfTable style={{ marginBottom: '1em' }}>
            <tbody>
              <tr>
                <PdfTdLabel>A list of the contributing manufacturers that the Sector EPD covers</PdfTdLabel>
                <td>{epdDefinition.listOfContributingManufacturers ?? NOT_ASSIGNED}</td>
              </tr>
              <tr>
                <td>
                  {isPcrV2
                    ? 'How the selection of the sites/products has been done and the average/worst-case has been determined'
                    : 'How the selection of the sites/products has been done and the average has been determined'}
                </td>
                <td>{epdDefinition.sitesSelectionDescription ?? NOT_ASSIGNED}</td>
              </tr>
              <tr>
                <td>
                  {isPcrV2
                    ? 'Statement about average/worst-case values'
                    : 'A statement that the EPD covers average values for an entire or partial product category specifying the percentage of representativeness and, hence, the declared product is an average that is not available for purchase on the market and that the results are not representative for any specific manufacturer or its product'}
                </td>
                <td>{epdDefinition.documentCoverageStatement ?? NOT_ASSIGNED}</td>
              </tr>
            </tbody>
          </PdfTable>
        </>
      );
    })(),

    isPcrV2 && (
      <>
        <PdfH1>Abbreviations</PdfH1>
        <PdfP dangerouslySetInnerHTML={{ __html: additionalInformation?.abbreviations?.replace(/\n/g, '') ?? '' }} />
      </>
    ),

    !isPcrV2 && !isFalsyOrEmptyHtmlRichText(additionalInformation?.conversion) && (
      <>
        <PdfH1>Conversion factors</PdfH1>
        <PdfP dangerouslySetInnerHTML={{ __html: additionalInformation.conversion.replace(/\n/g, '') }} />
      </>
    ),

    !isPcrV2 && !isFalsyOrEmptyHtmlRichText(additionalInformation?.dangerous) && (
      <>
        <PdfH1>Dangerous substances to indoor air, soil, and water during the use stage</PdfH1>
        <PdfP dangerouslySetInnerHTML={{ __html: additionalInformation.dangerous.replace(/\n/g, '') }} />
      </>
    ),
  ];

  const sections = possibleSections.filter(Boolean);
  if (sections.length === 0) {
    return null;
  }

  return (
    <PdfPage>
      <PdfGridsArea>
        {sections.map((s, i) => (
          <div key={i}>{s}</div>
        ))}
      </PdfGridsArea>
    </PdfPage>
  );
};

export default AdditionalInfoPage;
